import React, { useEffect, useState } from 'react';
import { OrderForm } from './components/OrderForm';
import { TranscriptionPane } from './components/TranscriptionPane';
export function App() {
  const [transcription, setTranscription] = useState<Array<{
    text: string;
    type: 'phone' | 'sms';
    sender?: 'customer' | 'csr';
  }>>([]);
  const [suggestions, setSuggestions] = useState({
    customerName: '',
    date: '',
    jobType: '',
    orderType: '',
    productType: '',
    quantity: '',
    estimate: '',
    address: '',
    email: '',
    contact: '' // Add new field
  });
  const [allSuggestionsGenerated, setAllSuggestionsGenerated] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    date: '',
    jobType: '',
    orderType: '',
    productCategory: '',
    selectedProducts: [] as Array<{
      productId: string;
      quantity: number;
    }>,
    estimate: '',
    address: ''
  });
  const handleSendPreview = () => {
    const previewMessage = `Order Preview:
- Customer: ${suggestions.customerName}
- Date: ${suggestions.date}
- Address: ${suggestions.address}
- Products: ${suggestions.quantity} x ${suggestions.productType}
- Estimate: ${suggestions.estimate}
Please reply "looks good" or "y" to confirm.`;
    setTranscription(prev => [...prev, {
      text: "CSR: I'm sending you a preview of the order for confirmation.",
      type: 'phone',
      sender: 'csr'
    }, {
      text: previewMessage,
      type: 'sms',
      sender: 'csr'
    }]);
    // Simulate customer response after 3 seconds
    setTimeout(() => {
      setTranscription(prev => [...prev, {
        text: 'looks good',
        type: 'sms',
        sender: 'customer'
      }]);
      setOrderConfirmed(true);
    }, 3000);
  };
  // Simulate incoming transcription
  useEffect(() => {
    const conversation = ['Customer: Hi, I need to rent a porta potty for a construction site.', 'CSR: Great! I can help you with that. Can I get your name please?', 'Customer: This is John Smith with ABC Construction.', 'CSR: Thanks Mr. Smith. When do you need the porta potty?', 'Customer: We need it next Monday.', 'CSR: And what type of unit are you looking for?', 'Customer: We need two standard units and one handicap accessible unit.', "CSR: And what's the address for delivery?", "Customer: It's 123 Main Street, Springfield, IL 62701.", 'CSR: Perfect. For this type of order, the estimate would be around $450. Does that work for you?', "Customer: Yes, that's fine.", 'CSR: Great. Just to confirm, this is for a construction job, correct?', "Customer: Yes, it's a new commercial building project.", "CSR: Perfect! I'll send you a quick summary of the order via text for your confirmation.",
    // Add new conversation flow after confirmation
    'Customer: looks good', 'CSR: Great! Could you please provide the email address and name of the person who should receive the invoice?', 'Customer: Yes, send it to john.smith@abcconstruction.com and I will be the point of contact.'];
    let index = 0;
    const interval = setInterval(() => {
      if (index < conversation.length) {
        setTranscription(prev => [...prev, {
          text: conversation[index],
          type: 'phone',
          sender: conversation[index].startsWith('Customer:') ? 'customer' : 'csr'
        }]);
        // Simulate AI extracting information
        if (index === 2) {
          setSuggestions(prev => ({
            ...prev,
            customerName: 'John Smith with ABC Construction'
          }));
        } else if (index === 4) {
          setSuggestions(prev => ({
            ...prev,
            date: '2023-11-06'
          }));
        } else if (index === 6) {
          const selectedProducts = [{
            productId: 'standard',
            quantity: 2
          }, {
            productId: 'handicap',
            quantity: 1
          }];
          setSuggestions(prev => ({
            ...prev,
            productType: 'Multiple Units Selected',
            selectedProducts
          }));
          setFormData(prev => ({
            ...prev,
            productCategory: 'portapotties',
            selectedProducts
          }));
        } else if (index === 8) {
          setSuggestions(prev => ({
            ...prev,
            address: '123 Main Street, Springfield, IL 62701'
          }));
        } else if (index === 9) {
          setSuggestions(prev => ({
            ...prev,
            estimate: '$450'
          }));
        } else if (index === 12) {
          setSuggestions(prev => ({
            ...prev,
            jobType: 'Construction',
            orderType: 'New Rental'
          }));
          setAllSuggestionsGenerated(true);
        } else if (index === 15) {
          setSuggestions(prev => ({
            ...prev,
            email: 'john.smith@abcconstruction.com',
            contact: 'John Smith'
          }));
        }
        index++;
      } else {
        clearInterval(interval);
        // Automatically trigger the preview after a short delay
        setTimeout(() => {
          handleSendPreview();
        }, 2000);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  return <main className="bg-background min-h-screen w-full" data-id="element-0">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b" data-id="element-1">
        <div className="p-4 flex items-center justify-between" data-id="element-2">
          <div className="flex items-center gap-3" data-id="element-3">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5L4mLNWZMx8TN4EuR5zQzU/image.png" alt="Logo" className="h-8 w-auto" data-id="element-4" />
            <h1 className="text-2xl font-bold text-foreground" data-id="element-5">Order Intake</h1>
          </div>
        </div>
      </header>
      <div className="p-4" data-id="element-6">
        <div className="flex flex-col gap-6" data-id="element-7">
          <div className="lg:hidden sticky top-[73px] z-40 -mx-4 px-4" data-id="element-8">
            <div className="bg-blue-50/80 shadow-lg rounded-lg" data-id="element-9">
              <TranscriptionPane transcription={transcription} mode="mixed" data-id="element-10" />
            </div>
          </div>
          <div className="flex flex-col lg:grid lg:grid-cols-5 gap-6" data-id="element-11">
            <div className="order-first lg:order-last lg:col-span-2 hidden lg:block" data-id="element-12">
              <TranscriptionPane transcription={transcription} mode="mixed" data-id="element-13" />
            </div>
            <div className="lg:col-span-3" data-id="element-14">
              <OrderForm suggestions={suggestions} onSendPreview={handleSendPreview} canSendPreview={allSuggestionsGenerated} orderConfirmed={orderConfirmed} formData={formData} setFormData={setFormData} data-id="element-15" />
            </div>
          </div>
        </div>
      </div>
    </main>;
}